import { breakpoints } from '@galapagos/design-system/breakpoints';
import { colors } from '@galapagos/design-system/colors';
import { radius } from '@galapagos/design-system/radius';
import { sizing } from '@galapagos/design-system/sizing';
import { spacing } from '@galapagos/design-system/spacing';
import { typography } from '@galapagos/design-system/typography';

type TypographyToken = keyof typeof typography;

export const DOWNLOAD_STYLE_ID = 'signup-download-style';

/** The class on the QR card: shown from the page-column breakpoint up. */
export const DOWNLOAD_QR_CLASS = 'signup-download__qr';

/**
 * The QR's drawn size. There is no QR token; the medium illustration size
 * matches v9's desktop QR and keeps the code large enough to scan from a screen.
 */
export const DOWNLOAD_QR_SIZE = sizing.illustration.md;

function font(token: TypographyToken): string {
  const t = typography[token];
  return `font-family: ${t.fontFamily}; font-weight: ${t.fontWeight}; font-size: ${t.fontSize}px; line-height: ${t.lineHeight}px; letter-spacing: ${t.letterSpacing}px;`;
}

/**
 * Layout for the download page pieces that have no design-system component:
 * the QR card's contents, the store badges, the rating summary and the review
 * list. Everything stays in the page column at every width, with room before
 * the footer. The QR card only shows on a desktop-width screen, decided here
 * by the page-column breakpoint rather than by guessing the device. Every
 * value is a design-system token.
 */
export function signupDownloadCss(): string {
  const bp = breakpoints.column;
  return `
.signup-download { display: flex; flex-direction: column; gap: ${spacing.m}px; padding-bottom: ${spacing.lg}px; }
.signup-download__card { display: flex; flex-direction: column; gap: ${spacing.xs}px; padding: ${spacing.m}px; background: ${colors.background.container}; border: ${sizing.stroke.hairline}px solid ${colors.stroke.divider}; border-radius: ${radius.xl}px; }
.signup-download__card--muted { background: ${colors.background.primary}; border-color: ${colors.background.primary}; }
.${DOWNLOAD_QR_CLASS} { display: none; }
.signup-download__qr-body { display: flex; align-items: center; gap: ${spacing.m}px; }
.signup-download__qr-code { flex-shrink: 0; width: ${DOWNLOAD_QR_SIZE}px; height: ${DOWNLOAD_QR_SIZE}px; }
.signup-download__qr-text { display: flex; flex-direction: column; gap: ${spacing.xs}px; }
.signup-download__badges { display: flex; flex-wrap: wrap; align-items: center; gap: ${spacing.s}px; }
.signup-download__badge { display: inline-flex; border-radius: ${radius.m}px; }
.signup-download__badge img { display: block; height: ${sizing.touchTarget.minimum}px; width: auto; }
.signup-download__rating { flex-direction: row; align-items: center; gap: ${spacing.m}px; }
.signup-download__rating-score { margin: 0; color: ${colors.text.primary}; ${font('h1Bold')} }
.signup-download__rating-text { display: flex; flex-direction: column; gap: ${spacing.xxs}px; }
.signup-download__rating-line { display: inline-flex; align-items: center; gap: ${spacing.s}px; }
.signup-download__reviews { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: ${spacing.m}px; }
@media (min-width: ${bp}px) {
  .${DOWNLOAD_QR_CLASS} { display: flex; }
}
`;
}

/** Injects the download stylesheet once per document. No-op during prerender. */
export function ensureDownloadStyle(): void {
  if (typeof document === 'undefined' || document.getElementById(DOWNLOAD_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = DOWNLOAD_STYLE_ID;
  style.textContent = signupDownloadCss();
  document.head.appendChild(style);
}
